import { useState, useRef, useEffect, useLayoutEffect } from "react";
import { Send, Loader2, ExternalLink, X, MapPin, ChevronDown, ChevronUp } from "lucide-react";
import { useUserContext } from "../context/UserContext";
import { sendChatMessage, type ChatMessage, type ProductResult } from "../services/AiChatService";
import { toast } from "react-toastify";
import { getProductImageUrl } from "../utils/imageUtils";

type Language = "en" | "es";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  products?: ProductResult[];
  timestamp: Date;
}

const retailerOptions = [
  { value: "all", label: "All Stores" },
  { value: "walmart", label: "Walmart" },
  { value: "walmart_online", label: "Online" },
  { value: "target", label: "Target" },
  { value: "bestbuy", label: "Best Buy" },
  { value: "home_depot", label: "HD" },
  { value: "woot", label: "Woot" },
];

const formatTimeAgo = (timestamp: number) => {
  const diff = Date.now() - timestamp;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(diff / 86400000)}d ago`;
};

const usePreventBodyScroll = () => {
  useLayoutEffect(() => {
    const isMobile = window.innerWidth < 768;
    if (!isMobile) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = originalOverflow; };
  }, []);
};

const welcomeMessages: Record<Language, string> = {
  en: "What deal can I help you find today?",
  es: "¿Qué oferta te ayudo a encontrar hoy?",
};

const detectBrowserLanguage = (): Language => {
  const browserLang = navigator.language || (navigator as any).userLanguage || "en";
  return browserLang.toLowerCase().startsWith("es") ? "es" : "en";
};

export default function AiChatPage() {
  usePreventBodyScroll();
  const { user } = useUserContext();
  const [language] = useState<Language>(detectBrowserLanguage);
  const [messages, setMessages] = useState<Message[]>(() => [
    {
      id: "welcome",
      role: "assistant",
      content: welcomeMessages[detectBrowserLanguage()],
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductResult | null>(null);
  const [selectedRetailers, setSelectedRetailers] = useState<string[]>(["all"]);
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(new Set());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const toggleRetailer = (value: string) => {
    if (value === "all") {
      setSelectedRetailers(["all"]);
    } else {
      setSelectedRetailers((prev) => {
        const newSelection = prev.filter((r) => r !== "all");
        if (newSelection.includes(value)) {
          const result = newSelection.filter((r) => r !== value);
          return result.length === 0 ? ["all"] : result;
        }
        return [...newSelection, value];
      });
    }
  };

  const toggleExpanded = (messageId: string) => {
    setExpandedMessages((prev) => {
      const newSet = new Set(prev);
      newSet.has(messageId) ? newSet.delete(messageId) : newSet.add(messageId);
      return newSet;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage: Message = { id: Date.now().toString(), role: "user", content: input.trim(), timestamp: new Date() };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    try {
      const history: ChatMessage[] = messages.filter((m) => m.id !== "welcome").map((m) => ({ role: m.role, content: m.content }));
      const retailers = selectedRetailers.includes("all") ? undefined : selectedRetailers;
      let messageWithContext = userMessage.content;
      if (retailers?.length) {
        const retailerNames = retailers.map(r => retailerOptions.find(o => o.value === r)?.label).filter(Boolean).join(", ");
        messageWithContext = `${userMessage.content} (searching in: ${retailerNames})`;
      }

      const response = await sendChatMessage({
        message: messageWithContext,
        zipCode: user?.zipCode || "",
        history: history.slice(-10),
        radiusKm: 30,
        language,
      });

      setMessages((prev) => [...prev, {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: response.message,
        products: response.products,
        timestamp: new Date(),
      }]);
    } catch (error) {
      console.error("Chat error:", error);
      toast.error(language === "es" ? "Error al obtener respuesta" : "Failed to get response");
      setMessages((prev) => [...prev, {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: language === "es" ? "Algo salió mal. Por favor intenta de nuevo." : "Something went wrong. Please try again.",
        timestamp: new Date(),
      }]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const formatPrice = (price: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(price);

  const getImageUrl = (imageUrl: string | undefined, retailer: string) => {
    if (!imageUrl) return "";
    if (retailer === "HD" && imageUrl.includes("<SIZE>")) return getProductImageUrl(imageUrl, "300");
    return getProductImageUrl(imageUrl, "150");
  };

  const ProductCard = ({ product }: { product: ProductResult }) => (
    <div
      className="flex gap-3 p-3 rounded-lg border border-gray-200 dark:border-dark-700 hover:bg-gray-50 dark:hover:bg-dark-800 cursor-pointer transition-colors"
      onClick={() => setSelectedProduct(product)}
    >
      {product.image ? (
        <img src={getImageUrl(product.image, product.retailer)} alt="" className="w-14 h-14 object-contain rounded bg-gray-50 dark:bg-dark-700" />
      ) : (
        <div className="w-14 h-14 rounded bg-gray-100 dark:bg-dark-700" />
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-900 dark:text-white line-clamp-2">{product.name}</p>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-sm font-semibold text-gray-900 dark:text-white">{formatPrice(product.price)}</span>
          {product.msrp && product.msrp > product.price && (
            <span className="text-xs text-gray-400 line-through">{formatPrice(product.msrp)}</span>
          )}
          {product.discount && product.discount > 0 && (
            <span className="text-xs text-green-600">-{product.discount}%</span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
          <span>{product.retailer}</span>
          {product.inStock !== undefined && (
            <span className={product.inStock ? "text-green-600" : "text-red-500"}>
              {product.inStock ? "In Stock" : "Out of Stock"}
            </span>
          )}
        </div>
      </div>
    </div>
  );

  const ProductModal = ({ product, onClose }: { product: ProductResult; onClose: () => void }) => (
    <div className="fixed inset-0 bg-black/50 z-[9999] flex items-end sm:items-center justify-center sheet-backdrop-in" onClick={onClose}>
      <div className="bg-white dark:bg-dark-800 w-full sm:max-w-md sm:rounded-xl rounded-t-xl max-h-[85vh] overflow-hidden sheet-panel-in-sm" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-dark-700">
          <span className="text-sm font-medium text-gray-500">{product.retailer}</span>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-dark-700 rounded">
            <X size={18} className="text-gray-400" />
          </button>
        </div>
        <div className="p-4">
          {product.image && (
            <div className="w-full h-40 flex items-center justify-center bg-gray-50 dark:bg-dark-700 rounded-lg mb-4">
              <img src={getImageUrl(product.image, product.retailer)} alt="" className="max-h-full object-contain" />
            </div>
          )}
          <h3 className="text-base font-medium text-gray-900 dark:text-white mb-2">{product.name}</h3>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xl font-semibold text-gray-900 dark:text-white">{formatPrice(product.price)}</span>
            {product.msrp && product.msrp > product.price && (
              <span className="text-sm text-gray-400 line-through">{formatPrice(product.msrp)}</span>
            )}
            {product.discount && product.discount > 0 && (
              <span className="text-sm text-green-600">-{product.discount}%</span>
            )}
          </div>
          {product.storeId && (
            <div className="text-sm text-gray-500 mb-2 space-y-1">
              {product.inStock !== undefined && (
                <p className={product.inStock ? "text-green-600" : "text-red-500"}>
                  {product.inStock ? "In Stock" : "Out of Stock"}
                </p>
              )}
              {product.lastUpdated && <p>Updated {formatTimeAgo(product.lastUpdated)}</p>}
              <p>Store #{product.storeId}</p>
            </div>
          )}
          {product.storeAddress && (
            <p className="text-sm text-gray-500 mb-4 flex items-center gap-1">
              <MapPin size={14} />
              {product.storeAddress}
            </p>
          )}
          {product.url && (
            <a
              href={product.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-2.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
            >
              <ExternalLink size={16} />
              View on {product.retailer}
            </a>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-full min-h-0 bg-white dark:bg-dark-900">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
          {messages.map((message) => {
            const isExpanded = expandedMessages.has(message.id);
            const visibleProducts = message.products?.slice(0, isExpanded ? undefined : 3) || [];
            const hasMore = (message.products?.length || 0) > 3;

            return (
              <div key={message.id}>
                {message.role === "assistant" ? (
                  <div className="flex gap-3">
                    <div className="w-7 h-7 rounded-full bg-primary-600 flex items-center justify-center flex-shrink-0 text-white text-xs font-bold">
                      H
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{message.content}</p>
                      {message.products && message.products.length > 0 && (
                        <div className="mt-4 space-y-2">
                          <p className="text-xs text-gray-500">{message.products.length} results</p>
                          {visibleProducts.map((product, idx) => (
                            <ProductCard key={`${product.sku}-${idx}`} product={product} />
                          ))}
                          {hasMore && (
                            <button
                              onClick={() => toggleExpanded(message.id)}
                              className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                            >
                              {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                              {isExpanded ? "Show less" : `Show ${message.products.length - 3} more`}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-3 justify-end">
                    <div className="max-w-[80%] bg-gray-100 dark:bg-dark-800 rounded-2xl px-4 py-2.5">
                      <p className="text-sm text-gray-800 dark:text-gray-200">{message.content}</p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {isLoading && (
            <div className="flex gap-3">
              <div className="w-7 h-7 rounded-full bg-primary-600 flex items-center justify-center flex-shrink-0 text-white text-xs font-bold">
                H
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 size={16} className="animate-spin" />
                {language === "es" ? "Buscando..." : "Searching..."}
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div className="flex-shrink-0 border-t border-gray-200 dark:border-dark-700 bg-white dark:bg-dark-900 pb-16 md:pb-0">
        <div className="max-w-2xl mx-auto px-4 py-3">
          {/* Filter Pills */}
          <div className="flex gap-1.5 mb-3 overflow-x-auto scrollbar-hide">
            {retailerOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => toggleRetailer(option.value)}
                className={`px-3 py-1 text-xs rounded-full whitespace-nowrap transition-colors ${
                  selectedRetailers.includes(option.value)
                    ? "bg-gray-900 dark:bg-white text-white dark:text-gray-900"
                    : "bg-gray-100 dark:bg-dark-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-dark-700"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          
          <form onSubmit={handleSubmit} className="flex gap-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={language === "es" ? `Buscar ofertas cerca de ${user?.zipCode || "ti"}...` : `Search deals near ${user?.zipCode || "you"}...`}
              className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-dark-800 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-dark-600 text-sm text-gray-900 dark:text-white placeholder-gray-500"
              rows={1}
              disabled={isLoading}
              style={{ minHeight: "44px" }}
            />
            <button
              type="submit"
              disabled={!input.trim() || isLoading}
              className="px-4 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-xl disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
            >
              {isLoading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
            </button>
          </form>
        </div>
      </div>

      {selectedProduct && <ProductModal product={selectedProduct} onClose={() => setSelectedProduct(null)} />}

      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}
